import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { OAuthService, AuthConfig, JwksValidationHandler } from 'angular-oauth2-oidc';
import { UserMailService } from './user-mail.service';

export const EMAIL_REQUIRED_LOGOUT_KEY = 'EmailRequiredLogout';
export const EMAIL_LOOKUP_ERROR_KEY = 'EmailLookupError';
export const EMAIL_LOOKUP_JDBC_ERROR = 'jdbc-connection';
export const EMAIL_LOOKUP_INTERNAL_ERROR = 'internal';

@Injectable({ providedIn: 'root' })
export class AppAuthService {

  private keycloakConfig: any = null;
  private CURRENT_USER_KEY = 'CurrentUser';

  constructor(private oauthService: OAuthService, private http: HttpClient,
              private userMailService: UserMailService) {}

  private isGovBrCallbackPath(): boolean {
    try {
      const path = (window.location.pathname || '').replace(/\/+$/, '');
      return path === '/govbr' || path.endsWith('/govbr');
    } catch (e) {
      return false;
    }
  }

  // Helpers for PKCE
  private generateRandomString(length: number) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    const array = new Uint32Array(length);
    window.crypto.getRandomValues(array);
    for (let i = 0; i < length; i++) {
      result += chars[array[i] % chars.length];
    }
    return result;
  }

  private generateCodeVerifier() {
    return this.generateRandomString(64);
  }

  private async sha256Base64Url(input: string) {
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    const hash = await window.crypto.subtle.digest('SHA-256', data);
    // convert hash to base64url
    const bytes = new Uint8Array(hash as ArrayBuffer);
    let str = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      str += String.fromCharCode(bytes[i]);
    }
    const base64 = btoa(str);
    // base64url
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }

  // Expose identity claims when available (from oauth library or manual id_token)
  getIdentityClaims(): any {
    try {
      const claims = (this.oauthService as any).getIdentityClaims ? (this.oauthService as any).getIdentityClaims() : null;
      if (claims) {
        return claims;
      }
      const id = sessionStorage.getItem('manual_oidc_id_token');
      if (id) {
        // decode JWT (naive)
        const parts = id.split('.');
        if (parts.length > 1) {
          return JSON.parse(atob(parts[1]));
        }
      }
    } catch (e) {
      // ignore
    }
    return null;
  }

  getUserRoles(): string[] {
    // Collect roles from both identity claims (ID token) and access token claims
    const collector = (c: any) => {
      const out: string[] = [];
      if (!c) { return out; }
      try {
        if (c.realm_access && Array.isArray(c.realm_access.roles)) {
          out.push(...c.realm_access.roles);
        }
      } catch (e) {}
      try {
        const ra = c.resource_access || {};
        if (this.keycloakConfig && this.keycloakConfig.clientId && ra[this.keycloakConfig.clientId] && Array.isArray(ra[this.keycloakConfig.clientId].roles)) {
          out.push(...ra[this.keycloakConfig.clientId].roles);
        }
        for (const k of Object.keys(ra)) {
          if (ra[k] && Array.isArray(ra[k].roles)) {
            out.push(...ra[k].roles);
          }
        }
      } catch (e) {}
      return out;
    };

    const roles: string[] = [];
    // ID token / identity claims
    const idClaims = this.getIdentityClaims();
    roles.push(...collector(idClaims));

    // Access token: prefer oauthService.getAccessToken(), fallback to manual stored token
    try {
      let at: string | null = this.oauthService.getAccessToken() || null;
      if (!at) {
        at = sessionStorage.getItem('manual_oidc_access_token');
      }
      if (at) {
        const parts = at.split('.');
        if (parts.length > 1) {
          try {
            const payload = JSON.parse(atob(parts[1]));
            roles.push(...collector(payload));
          } catch (e) {
            // ignore invalid token decoding
          }
        }
      }
    } catch (e) {
      // ignore access token parsing errors
    }

    // dedupe and return
    return Array.from(new Set(roles.map(r => (r || '').toString().trim()).filter(r => !!r)));
  }

  init(keycloakConfig: any): Promise<void> {
  this.keycloakConfig = keycloakConfig;
    const issuer = (keycloakConfig && keycloakConfig.url) ? `${keycloakConfig.url.replace(/\/$/, '')}/realms/${keycloakConfig.realm}` : null;

    const authConfig: AuthConfig = {
      issuer: issuer || undefined,
      clientId: keycloakConfig.clientId,
      redirectUri: window.location.origin + '/',
      responseType: 'code',
      scope: 'openid profile email',
      showDebugInformation: false,
      oidc: true,
      requireHttps: false
    };

    // Force specific endpoint URLs based on configured keycloak base URL to avoid discovery pointing to a different host/port
    try {
      const base = keycloakConfig.url.replace(/\/$/, '');
      const realm = keycloakConfig.realm;
      // Keycloak v17+ (Quarkus distribution) exposes endpoints under '/realms' (no '/auth' prefix).
      // Use the '/realms' path which is compatible with Keycloak v25. If your Keycloak server
      // actually uses '/auth/realms', adjust this value in the runtime config.
      authConfig.loginUrl = `${base}/realms/${realm}/protocol/openid-connect/auth`;
      authConfig.logoutUrl = `${base}/realms/${realm}/protocol/openid-connect/logout`;
      authConfig.tokenEndpoint = `${base}/realms/${realm}/protocol/openid-connect/token`;
      authConfig.userinfoEndpoint = `${base}/realms/${realm}/protocol/openid-connect/userinfo`;
    } catch (e) {
      // ignore and fall back to discovery
    }

    this.oauthService.configure(authConfig);
    // Use sessionStorage to avoid persisting tokens across browser restarts
    this.oauthService.setStorage(sessionStorage);
    this.oauthService.tokenValidationHandler = new JwksValidationHandler();

    // O callback do Gov.br também utiliza code/state. Não permita que o
    // angular-oauth2-oidc tente validá-los como se fossem do Keycloak.
    if (this.isGovBrCallbackPath()) {
      return this.oauthService.loadDiscoveryDocument()
        .then(() => Promise.resolve())
        .catch(() => Promise.resolve());
    }

    return this.oauthService.loadDiscoveryDocumentAndTryLogin().then(async () => {
      const foundToken = this.oauthService.getAccessToken();

      // If the URL contains OAuth2 error params (Keycloak may redirect with error=login_required)
      // avoid automatically re-triggering login flows which can cause a redirect loop.
      const href = window.location.href || '';
      const hasErrorParam = /([#?].*error=)/.test(href) || /redirect_fragment=/.test(href);
  if (hasErrorParam && !foundToken) {
  // detected error in URL — avoid auto-login to prevent loop
        // Remove error fragments from URL to avoid repeated parsing in subsequent loads
        try {
          const clean = href.replace(/(\?|&)?redirect_fragment=[^&]*/g, '')
                             .replace(/([#&]?error=[^&]*)/g, '')
                             .replace(/([#&]?state=[^&]*)/g, '')
                             .replace(/([#&]?iss=[^&]*)/g, '');
          history.replaceState(null, document.title, clean);
          } catch (e) {
          // ignore
        }
        return;
      }

      // If a PKCE code is present in the URL and a code_verifier was stored, perform token exchange manually
      try {
        return this.performManualTokenExchangeIfPresent().then(async () => {
          // After any token exchange, populate CurrentUser for backward compatibility
          try { await this.populateCurrentUserFromClaims(); } catch (e) {}
        });
      } catch (e) {
        console.error('AppAuthService: error checking for code in URL', e);
      }

      // Also populate CurrentUser if discovery flow already provided tokens
      try { await this.populateCurrentUserFromClaims(); } catch (e) {}

      return;
    }).catch(err => {
      // error loading discovery or trying login
      // If the library failed due to invalid nonce/state we may still have started a manual PKCE flow
      // in which case attempt manual token exchange if possible. This helps when discovery-based
      // processing validated state/nonce against different keys than our manual flow.
      try {
        if (err && err.type && err.type === 'invalid_nonce_in_state') {
          // detected invalid_nonce_in_state, attempting manual exchange fallback
          return this.performManualTokenExchangeIfPresent().then(async () => {
            try { await this.populateCurrentUserFromClaims(); } catch (e) {}
            return Promise.resolve();
          }).catch(() => Promise.resolve());
        }
      } catch (e) {
        // ignore
      }

      // In any case, clean URL fragments that may confuse Angular Router (avoid paths like /usuario-interno&iss)
      try {
        const href = window.location.href || '';
        const clean = window.location.origin + window.location.pathname;
        history.replaceState(null, document.title, clean);
      } catch (e) {
        // ignore
      }

      return Promise.resolve();
    });
  }

  private async performManualTokenExchangeIfPresent(): Promise<void> {
    try {
      // Prefer callback params captured before Angular boot (index.html) to avoid router confusion
      const storedCallbackCode = sessionStorage.getItem('oidc_callback_code');
      const storedCallbackState = sessionStorage.getItem('oidc_callback_state');
      const storedCallbackIss = sessionStorage.getItem('oidc_callback_iss');

      const params = new URLSearchParams(window.location.search);
      let code = storedCallbackCode || params.get('code');
      // Also check fragment if present (some flows use redirect_fragment)
      if (!code && window.location.hash) {
        const hash = window.location.hash.replace(/^#/, '');
        const hp = new URLSearchParams(hash);
        code = hp.get('code');
      }
      const storedVerifier = sessionStorage.getItem('oidc_code_verifier');
      const storedState = sessionStorage.getItem('oidc_state');
      const returnedState = storedCallbackState || params.get('state') || null;
      if (code && storedVerifier) {
        // Optional: verify state matches
        if (storedState && returnedState && storedState !== returnedState) {
          // state mismatch on token exchange - abort and clean PKCE artifacts to be safe
          try {
            sessionStorage.removeItem('oidc_code_verifier');
            sessionStorage.removeItem('oidc_state');
            sessionStorage.removeItem('oidc_nonce');
            sessionStorage.removeItem('oidc_callback_code');
            sessionStorage.removeItem('oidc_callback_state');
            sessionStorage.removeItem('oidc_callback_iss');
            sessionStorage.removeItem('oidc_redirect_uri');
          } catch (e) {
            // ignore storage cleanup errors
          }
          console.warn('AppAuthService: state mismatch detected during manual token exchange — aborting');
          return;
        }
        const base = this.keycloakConfig.url.replace(/\/$/, '');
        const realm = this.keycloakConfig.realm;
        const tokenUrl = `${base}/realms/${realm}/protocol/openid-connect/token`;
  const redirectUri = sessionStorage.getItem('oidc_redirect_uri') || window.location.origin + '/';
        const body = new URLSearchParams();
        body.set('grant_type', 'authorization_code');
        body.set('code', code);
        body.set('redirect_uri', redirectUri);
        body.set('client_id', this.keycloakConfig.clientId);
        body.set('code_verifier', storedVerifier);

        const response = await this.http.post<any>(tokenUrl, body.toString(), {
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        }).toPromise();


        // store tokens for manual flow
        try {
          sessionStorage.setItem('manual_oidc_access_token', response.access_token || '');
          if (response.id_token) {
            sessionStorage.setItem('manual_oidc_id_token', response.id_token);
          }
          if (response.refresh_token) {
            sessionStorage.setItem('manual_oidc_refresh_token', response.refresh_token);
          }
        } catch (e) {
          console.error('AppAuthService: error storing tokens', e);
        }
        // Also mirror tokens under the keys expected by angular-oauth2-oidc so the library
        // can recognize the tokens as if discovery had completed normally.
        try {
          sessionStorage.setItem('access_token', response.access_token || '');
          if (response.id_token) {
            sessionStorage.setItem('id_token', response.id_token);
          }
          if (response.refresh_token) {
            sessionStorage.setItem('refresh_token', response.refresh_token);
          }
        } catch (e) {
          // best-effort: if we can't mirror tokens, continue (manual_* tokens remain available)
        }
        // populate CurrentUser from claims (id_token or access token)
        try { this.populateCurrentUserFromClaims(); } catch (e) {}
        // clean up verifier and state
        sessionStorage.removeItem('oidc_code_verifier');
        sessionStorage.removeItem('oidc_state');
        sessionStorage.removeItem('oidc_nonce');
  // also clean callback capture keys
  sessionStorage.removeItem('oidc_callback_code');
  sessionStorage.removeItem('oidc_callback_state');
  sessionStorage.removeItem('oidc_callback_iss');
    sessionStorage.removeItem('oidc_redirect_uri');
        // remove code and state from URL to avoid loops and router confusion
        try {
          const cleanUrl = window.location.origin + window.location.pathname;
          history.replaceState(null, document.title, cleanUrl);
          } catch (e) {
          // ignore
        }
        return;
      }
      } catch (e) {
      // token exchange failed or not present
    }
    return;
  }

  login() {
    this.oauthService.initCodeFlow();
  }

  private registrarLogoutPorEmail(): void {
    try {
      sessionStorage.setItem(EMAIL_REQUIRED_LOGOUT_KEY, 'true');
      sessionStorage.setItem(EMAIL_LOOKUP_ERROR_KEY, 'null');
    } catch (e) {}
  }

  private registrarErroNaObtencaoEmail(error: any): void {
    let message = '';
    if (error && error.error) {
      if (typeof error.error.message === 'string') {
        message = error.error.message;
      } else if (typeof error.error === 'string') {
        message = error.error;
      }
    }

    const reason = message.indexOf('Unable to acquire JDBC Connection; nested exception') >= 0
      ? EMAIL_LOOKUP_JDBC_ERROR
      : EMAIL_LOOKUP_INTERNAL_ERROR;

    try {
      sessionStorage.setItem(EMAIL_REQUIRED_LOGOUT_KEY, 'null');
      sessionStorage.setItem(EMAIL_LOOKUP_ERROR_KEY, reason);
    } catch (e) {}
  }

  logout(redirectUri?: string) {
    // Capture id_token (if present) before clearing storages so we can include it
    // as id_token_hint when calling Keycloak end-session endpoint.
    let idToken: string | null = null;
    try {
      idToken = (this.oauthService as any).getIdToken ? (this.oauthService as any).getIdToken() : null;
    } catch (e) {
      idToken = null;
    }
    try {
      if (!idToken) {
        idToken = sessionStorage.getItem('manual_oidc_id_token') || sessionStorage.getItem('id_token') || null;
      }
    } catch (e) {
      idToken = null;
    }

    // Clear local/session state
    try {
      localStorage.removeItem(this.CURRENT_USER_KEY);
    } catch (e) {}
    try {
      sessionStorage.removeItem('manual_oidc_access_token');
      sessionStorage.removeItem('manual_oidc_id_token');
      sessionStorage.removeItem('manual_oidc_refresh_token');
      // Also remove mirrored tokens that may have been written for angular-oauth2-oidc
      try {
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('id_token');
        sessionStorage.removeItem('refresh_token');
      } catch (e) {
        // ignore
      }
    } catch (e) {}

    // If a redirectUri is provided and we have keycloak config, construct the Keycloak logout URL
    try {
      if (redirectUri && this.keycloakConfig && this.keycloakConfig.url && this.keycloakConfig.realm) {
        const base = this.keycloakConfig.url.replace(/\/$/, '');
        const realm = this.keycloakConfig.realm;
        let logoutUrl = `${base}/realms/${realm}/protocol/openid-connect/logout?post_logout_redirect_uri=${encodeURIComponent(redirectUri)}`;
        if (idToken) {
          // include id_token_hint when available to satisfy Keycloak realms that require it
          logoutUrl += `&id_token_hint=${encodeURIComponent(idToken)}`;
        }
        window.location.href = logoutUrl;
        return;
      }
    } catch (e) {
      // fall back to oauthService.logOut()
    }

    this.oauthService.logOut();
  }

  /**
   * Build a lightweight CurrentUser object from available token claims and store it in localStorage
   * for backward compatibility with the older Keycloak-based flow.
   */
  async populateCurrentUserFromClaims(): Promise<void> {
    try {
      const claims = this.getIdentityClaims();
      // If ID token claims are not present, attempt to parse manual id token stored in sessionStorage
      let name = null;
      let username = null;
      let email = null;
      let isAuthenticated = false;

      if (claims) {
        name = claims.name || claims.given_name || null;
        username = claims.preferred_username || claims.sub || null;
        email = claims.email || null;
        isAuthenticated = true;
      } else {
        // Try manual stored id token (sessionStorage)
        try {
          const raw = sessionStorage.getItem('manual_oidc_id_token');
          if (raw) {
            const parts = raw.split('.');
            if (parts.length > 1) {
              const payload = JSON.parse(atob(parts[1]));
              name = payload.name || payload.given_name || null;
              username = payload.preferred_username || payload.sub || null;
              email = payload.email || null;
              isAuthenticated = true;
            }
          }
        } catch (e) {}
      }

      if (isAuthenticated && (!email || !email.trim())) {
        if (!username || !username.trim()) {
          this.registrarLogoutPorEmail();
          this.logout();
          return;
        }
        let response;
        try {
          response = await this.userMailService.buscarEmail(username).toPromise();
        } catch (e) {
          if (e && e.status === 500) {
            this.registrarErroNaObtencaoEmail(e);
          } else {
            this.registrarLogoutPorEmail();
          }
          this.logout();
          return;
        }

        if (!response || !response.email || !response.email.trim()) {
          this.registrarLogoutPorEmail();
          this.logout();
          return;
        }

        if (!response.username || !response.username.trim()) {
          this.logout();
          return;
        }
        email = response.email;
      }

      if (isAuthenticated) {
        const obj = { nome: name || '', username: username || '', email: email || '', isAuthenticated: true };
        try {
          localStorage.setItem(this.CURRENT_USER_KEY, JSON.stringify(obj));
        } catch (e) {}
      }
    } catch (e) {
      // ignore
    }
  }

  getAccessToken(): string {
    return this.oauthService.getAccessToken();
  }

  hasValidAccessToken(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  /**
   * Initiates the authorization code + PKCE flow using an explicit redirect URI.
   * If no redirectUri is provided, the configured redirectUri (from init) will be used.
   */
  async loginWithRedirect(redirectUri?: string, options?: { prompt?: string; kc_idp_hint?: string }) {
    const extraParams: any = {};
    if (redirectUri) {
      extraParams['redirect_uri'] = redirectUri;
    }
    // Rate-limit login attempts to avoid redirect loops. If a login was attempted in the last 60s, skip.
    try {
      const last = Number(sessionStorage.getItem('oidc_last_login_attempt') || '0');
      const now = Date.now();
      if (last && now - last < 60_000) {
        console.warn('AppAuthService.loginWithRedirect: login attempted recently, skipping to avoid loop');
        return;
      }
      sessionStorage.setItem('oidc_last_login_attempt', String(now));
    } catch (e) {
      // ignore storage errors
    }
    // Log debug info and where we will redirect
    try {
      // Mark that the app initiated an OIDC flow so the pre-bootstrap capture code can
      // safely decide whether to act. This avoids capturing unrelated query params.
      try { sessionStorage.setItem('oidc_flow_started', '1'); } catch (e) {}
  const discoveryAuth = ((this.oauthService as any).discoveryDocument && (this.oauthService as any).discoveryDocument.authorization_endpoint) || null;
  // discovery authorization endpoint and redirectUri are available in authConfig
      // If discovery failed or we want to bypass the library, build a manual PKCE authorize URL from configured keycloakConfig
      if (!discoveryAuth && this.keycloakConfig) {
        try {
          // Generate PKCE code_verifier and code_challenge
          const codeVerifier = this.generateCodeVerifier();
          const codeChallenge = await this.sha256Base64Url(codeVerifier);
          const base = this.keycloakConfig.url.replace(/\/$/, '');
          const realm = this.keycloakConfig.realm;
          const clientId = this.keycloakConfig.clientId;
          const state = this.generateRandomString(24);
          const nonce = this.generateRandomString(24);
          // store verifier/state/nonce for token exchange
          try {
            sessionStorage.setItem('oidc_code_verifier', codeVerifier);
            sessionStorage.setItem('oidc_state', state);
            sessionStorage.setItem('oidc_nonce', nonce);
            // store redirect uri used so token endpoint validation matches it
            sessionStorage.setItem('oidc_redirect_uri', (redirectUri || window.location.origin + '/'));
          } catch (e) {
            console.error('AppAuthService: could not store PKCE verifier/state', e);
          }

          let url = `${base}/realms/${realm}/protocol/openid-connect/auth?response_type=code&client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(redirectUri || window.location.origin + '/')}&scope=openid%20profile%20email&state=${encodeURIComponent(state)}&nonce=${encodeURIComponent(nonce)}&code_challenge=${encodeURIComponent(codeChallenge)}&code_challenge_method=S256`;
          if (options && options.prompt) {
            url += `&prompt=${encodeURIComponent(options.prompt)}`;
          }
          if (options && options.kc_idp_hint) {
            url += `&kc_idp_hint=${encodeURIComponent(options.kc_idp_hint)}`;
          }
          // redirecting to fallback PKCE authorize URL
          try { sessionStorage.setItem('oidc_flow_started', '1'); } catch (e) {}
          window.location.href = url;
          return;
        } catch (e) {
          console.error('AppAuthService: error building fallback authorize URL', e);
        }
      }
      // initCodeFlow accepts (state?, params?) where params can include redirect_uri
      // This will redirect the browser to the configured Keycloak OIDC authorize endpoint.
      try { sessionStorage.setItem('oidc_flow_started', '1'); } catch (e) {}
      this.oauthService.initCodeFlow(undefined, extraParams);
    } catch (e) {
      console.error('AppAuthService.loginWithRedirect error', e);
      throw e;
    }
  }

}
